import { Button, ButtonSizeType } from '@design-system';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { isPast } from 'date-fns';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useEffect } from 'react';

import { FF_PRODUCT_TILE_SUBSCRIPTION_BUTTON } from '../../../constants/featureFlags';
import { skuRef } from '../../../helpers/purchasing';
import { useIsSubscribed } from '../../../hooks/subscription';
import { useAuthenticate } from '../../../hooks/useAuthenticate';
import { useIsPreOrder } from '../../../hooks/useIsPreOrder';
import { useAuth } from '../../../providers';
import { useDraws } from '../../../providers/DrawProvider';
import { usePreAuthDrawCtx } from '../../../providers/PreAuthDrawProvider';
import { useStore } from '../../../store/utils';
import { useAnalyticsEmitter } from '../../../utils/analytics/emitter';
import { buildSubscriptions } from '../../../utils/subscriptions';
import { AuthEntryPoint } from '../../auth/types';
import { useDrawButtonLabels } from '../hooks/useDrawButtonLabels';
import { DrawWithFrames } from '../types';

type Props = {
  buttonLocation: 'dialogCta' | 'mainCta' | 'stickyCta';
  drawWithFrames: DrawWithFrames;
  startDrawFlow: () => void;
  hasCheckoutParam?: boolean;
  size?: ButtonSizeType;
  fullWidth?: boolean;
};

export const PreAuthDrawButton: React.FC<Props> = ({
  buttonLocation,
  drawWithFrames,
  fullWidth = true,
  hasCheckoutParam = false,
  size = 'lg',
  startDrawFlow,
}) => {
  const { draw, frames, product } = drawWithFrames;
  const { isLoggedIn } = useAuth();
  const { isAccountInDraw } = useDraws();
  const [emitter] = useAnalyticsEmitter();
  const { t } = useTranslation(['product', 'authentication']);
  const { authenticateAndSubscribe } = useAuthenticate();
  const isPreOrder = useIsPreOrder(draw);
  const experimentalProductTileSubscriptionButton = useFeatureIsOn(
    FF_PRODUCT_TILE_SUBSCRIPTION_BUTTON,
  );

  const drawEntry = useStore((s) => s.features.draw.drawEntry[draw.id]);
  const {
    activeDraw,
    customerSelectedFrame,
    loading,
    setupComplete,
    setupController,
  } = usePreAuthDrawCtx();

  const labels = useDrawButtonLabels(buttonLocation, isPreOrder);

  const drawVariant = product.variants.find((v) => v.title === 'Draw');

  const subscriptions = buildSubscriptions({
    'artists': product.artists.map((a) => ({
      name: a.name,
      internalId: a.id,
    })),
    'isPreOrder': isPreOrder,
    'productId': draw.productId,
    'productRef': skuRef(drawVariant?.sku ?? ''),
    'productTitle': product.title,
  });

  const { isSubscribedAll, refetch } = useIsSubscribed(
    subscriptions.map(({ objectId, type }) => ({ objectId, type })),
  );

  const start = useCallback(async () => {
    if (!setupComplete || activeDraw?.draw.id !== draw.id) {
      await setupController(drawWithFrames);
    }
    startDrawFlow();
  }, [
    activeDraw?.draw.id,
    draw.id,
    drawWithFrames,
    setupComplete,
    setupController,
    startDrawFlow,
  ]);

  const enterDraw = useCallback(() => {
    if (isLoggedIn && isSubscribedAll) {
      start();
      return;
    }
    authenticateAndSubscribe({
      authScreenThumbnail: product.thumbnail?.url,
      authScreenTitle: isPreOrder
        ? t('authentication:dynamicTitle.preorder')
        : t('authentication:dynamicTitle.draw'),
      analyticsProperties: {
        'artist': product.artists[0]?.name ?? '',
        'artist name': product.artists[0]?.name ?? '',
        'button_location': 'product page',
        'product_id': draw.productId,
        'product_status': 'coming soon',
        'product_title': product.title,
        'source': isPreOrder
          ? AuthEntryPoint.PreOrder
          : AuthEntryPoint.DrawEntry,
      },
      hideSuccessScreen: true,
      onFlowComplete: () => {
        setTimeout(() => {
          start();
          refetch();
        }, 150);
      },
      optInGeneral: false,
      subscriptions,
    });

    if (isLoggedIn) {
      start();
    }
  }, [
    authenticateAndSubscribe,
    draw.productId,
    isLoggedIn,
    isPreOrder,
    isSubscribedAll,
    product.artists,
    product.thumbnail?.url,
    product.title,
    refetch,
    start,
    subscriptions,
    t,
  ]);

  useEffect(() => {
    if (hasCheckoutParam && buttonLocation === 'mainCta') {
      enterDraw();
    }
  }, [buttonLocation, hasCheckoutParam]);

  if (isLoggedIn && (drawEntry?.entered || isAccountInDraw(draw.id))) {
    return (
      <Button
        disabled
        fullWidth={fullWidth}
        icon="ic_check"
        label={t(labels.success)}
        size={size}
        variant="positive"
      />
    );
  }

  return (
    <Button
      disabled={isPast(new Date(draw.closeAt))}
      fullWidth={fullWidth}
      label={t(labels.normal)}
      loading={loading(draw.id)}
      onClick={() => {
        emitter(isPreOrder ? 'pre-order' : 'draw entry', 'clicked', {
          'product title': product.title,
          'artist name': product.artists[0]?.name ?? '',
          'product price': product.price,
          'product can be framed': Boolean(frames?.length),
          'product frame upgrade available': Boolean(frames),
          // TODO
          'product frame chosen': Boolean(customerSelectedFrame),
          experimentalProductTileSubscriptionButton,
        });
        enterDraw();
      }}
      size={size}
      variant="primary"
    />
  );
};
